import { Modal } from "tailwindcss-stimulus-components";

export default class ResponseModal extends Modal {
  static targets = ["dialog", "frame", "prevButton", "nextButton", "prevHistoryButton", "nextHistoryButton"];
  connect() {
    super.connect();
    this.responseIds = [];
    this.currentResponseIndex = -1;
    this.handleKeyDownBound = this.handleKeyDown.bind(this);
    this.handleEscapeBound = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close(event);
      }
    };
    if (this.hasDialogTarget) {
      this.dialogTarget.addEventListener('keydown', this.handleKeyDownBound);
      this.dialogTarget.addEventListener('keydown', this.handleEscapeBound);
    }
  }

  disconnect() {
    if (this.hasDialogTarget) {
      this.dialogTarget.removeEventListener('keydown', this.handleKeyDownBound);
      this.dialogTarget.removeEventListener('keydown', this.handleEscapeBound);
    }
    super.disconnect();
  }

  getResponseIdFromEvent(event) {
    if (!event?.target) return null;
    let responseId = event.target.dataset.responseId;
    if (!responseId) {
      const parent = event.target.closest('[data-response-id]');
      responseId = parent ? parent.dataset.responseId : null;
    }
    console.log("Response ID from event:", responseId);
    return responseId;
  }

  getImageIdFromEvent(event) {
    if (!event?.target) return null;
    let imageId = event.target.dataset.imageId;
    if (!imageId) {
      const parent = event.target.closest('[data-image-id]');
      imageId = parent ? parent.dataset.imageId : null;
    }
    console.log("Image ID from event:", imageId);
    return imageId;
  }

  highlightCurrentResponse() {
    // Remove highlight from all and restore original borders
    document.querySelectorAll('.border-sapphire').forEach(el => {
      el.classList.remove('border-2', 'border-sapphire');
      // Restore original border if it was an image container
      if (el.classList.contains('border-gray-100')) {
        el.classList.add('border');
      }
    });
    // Highlight the current
    const id = this.responseIds[this.currentResponseIndex];
    if (!id) return;
    const el = document.querySelector(`[data-response-id='${id}']`);
    if (el) {
      // Remove existing border classes to avoid conflicts
      el.classList.remove('border', 'border-gray-100');
      el.classList.add('border-sapphire', 'border-2');
    }
  }

  open(event) {
    // do nothing if the div, or parent of the div being clicked has a data-disable-response-modal attribute
    if (event?.target?.closest('[data-disable-response-modal]')) {
      return;
    }

    // Don't open modal if user clicked a link - let navigation happen normally
    // This prevents hovercards and other embedded links from triggering the modal
    if (event?.target?.closest('a')) {
      return;
    }
    super.open(event);

    // Scope response ID collection to the table/container that was clicked in,
    // not the whole document. This prevents picking up IDs from other tables
    // (e.g. mission show page behind a mission task modal dialog).
    // [data-response-modal-scope] is a plain marker attribute (no Stimulus
    // controller attached) used by the Rated Images Gallery modal (#2969) so
    // chevron nav stays scoped to that gallery's own images rather than
    // falling through to `document` and picking up the whole page behind it.
    const clickedContainer = event?.target?.closest('[data-controller="table"], table, [data-controller="album"], [data-response-modal-scope]')
      || event?.target?.closest('tbody')?.closest('table')
      || document;
    this._responseIdScope = clickedContainer;

    // Collect all response IDs from the scoped container, only visible elements
    const allResponseIds = Array.from(clickedContainer.querySelectorAll('tr[data-response-id], div[data-response-id]'))
      .filter(elements => elements.style.display !== 'none') // Only include visible elements
      .map(el => el.dataset.responseId)
      .filter(Boolean);

    // For images view, deduplicate response IDs while preserving order
    if (this.frameTarget.id === 'images-response-modal') {
      this.responseIds = [...new Set(allResponseIds)]; // Remove duplicates, preserve order
      console.log('Images modal opened:', {
        frameId: this.frameTarget.id,
        allResponseIds: allResponseIds,
        deduplicatedResponseIds: this.responseIds
      });
    } else {
      this.responseIds = allResponseIds; // Keep original behavior for other contexts
      console.log('Other modal opened:', {
        frameId: this.frameTarget.id,
        responseIds: this.responseIds
      });
    }
    const responseId = this.getResponseIdFromEvent(event);
    const imageId = this.getImageIdFromEvent(event);
    this.currentResponseIndex = this.responseIds.indexOf(responseId);
    console.log('Modal setup complete:', {
      clickedResponseId: responseId,
      clickedImageId: imageId,
      currentResponseIndex: this.currentResponseIndex,
      totalResponses: this.responseIds.length
    });
    if (responseId) {
      this.triggerTurboFrameReload(responseId, imageId);
      localStorage.setItem("openModalResponseId", responseId);
      if (imageId) {
        localStorage.setItem("openModalImageId", imageId);
      }
    }
    // Don't disable navigation for images view anymore - we want response navigation to work
    this.disableNavigation = false;
    // Listen for turbo:frame-load to update nav buttons after frame loads
    if (this.hasFrameTarget) {
      this.frameTarget.addEventListener('turbo:frame-load', this.handleFrameLoad);
    }
    this.highlightCurrentResponse();
    // Only update nav buttons if they exist (they're loaded with the turbo frame)
    if (this.hasPrevButtonTarget && this.hasNextButtonTarget) {
      this.updateNavButtons();
    }
    // Focus dialog for keyboard events
    if (this.hasDialogTarget) {
      this.dialogTarget.focus();
    }
  }

  handleFrameLoad = () => {
    this.updateNavButtons();
    this.highlightCurrentResponse();
  }

  close(event) {
    // prevent default close behavior so a page refresh doesn't happen
    if (event && event.preventDefault) {
      event.preventDefault();
    }

    super.close(event);
    // Scroll the last previewed record into view
    const id = this.responseIds[this.currentResponseIndex];
    if (id) {
      const el = document.querySelector(`[data-response-id='${id}']`);
      if (el) {
        // Use the original working scrollIntoView but with 'nearest' to be less aggressive
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });

        // Small adjustment after scroll to ensure it's not hidden
        setTimeout(() => {
          const rect = el.getBoundingClientRect();
          if (rect.top < 100) { // If element is too close to top (within 100px)
            window.scrollBy({
              top: -80, // Scroll up 80px to create space
              behavior: 'smooth'
            });
          }
        }, 200);
      }
    }
    // Always clear the openModalResponseId and openModalImageId from localStorage
    localStorage.removeItem("openModalResponseId");
    localStorage.removeItem("openModalImageId");
    if (this.hasFrameTarget) {
      this.frameTarget.removeEventListener('turbo:frame-load', this.handleFrameLoad);
    }
  }

  triggerTurboFrameReload(responseId, imageId = null) {
    let url = `/responses/${responseId}/preview.turbo_stream?frame=${this.frameTarget.id}`;
    if (imageId) {
      url += `&image_id=${imageId}`;
    }
    this.frameTarget.setAttribute('src', url);
  }

  refreshVisibleResponseIds() {
    // Get the current response ID before refreshing the list
    const currentResponseId = this.responseIds[this.currentResponseIndex];

    // Refresh the list from the same scoped container used on open
    const scope = this._responseIdScope || document;
    const allResponseIds = Array.from(scope.querySelectorAll('tr[data-response-id], div[data-response-id]'))
      .filter(elements => elements.style.display !== 'none') // Only include visible elements
      .map(el => el.dataset.responseId)
      .filter(Boolean);

    // For images view, deduplicate response IDs while preserving order
    if (this.frameTarget.id === 'images-response-modal') {
      this.responseIds = [...new Set(allResponseIds)]; // Remove duplicates, preserve order
    } else {
      this.responseIds = allResponseIds; // Keep original behavior for other contexts
    }

    // Update the current index to match the current response ID in the new list
    this.currentResponseIndex = this.responseIds.indexOf(currentResponseId);

    // If the current response is no longer visible, we might need to handle this case
    if (this.currentResponseIndex === -1 && this.responseIds.length > 0) {
      this.currentResponseIndex = 0; // Default to first visible item
    }
  }

  navigateToResponse(index) {
    if (index < 0 || index >= this.responseIds.length) return;
    const responseId = this.responseIds[index];
    this.triggerTurboFrameReload(responseId);
    localStorage.setItem("openModalResponseId", responseId);
    this.currentResponseIndex = index;
    this.updateNavButtons();
    this.highlightCurrentResponse();
  }

  flashChevron(direction) {
    let btn = null;
    if (direction === 'left' && this.hasPrevButtonTarget) {
      btn = this.prevButtonTarget;
    } else if (direction === 'right' && this.hasNextButtonTarget) {
      btn = this.nextButtonTarget;
    }
    if (btn) {
      // Save original classes and content
      const originalClasses = btn.getAttribute('data-original-classes') || btn.className;
      const originalContent = btn.innerHTML;
      btn.setAttribute('data-original-classes', originalClasses);
      btn.setAttribute('data-original-content', originalContent);

      // Replace content with spinner
      btn.innerHTML = `
        <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      `;

      // Add loading state classes
      btn.classList.remove('bg-white/80', 'text-gray-700', 'hover:bg-white');
      btn.classList.add('bg-sapphire', 'text-white');

      // Restore original state after turbo frame loads
      const restoreButton = () => {
        btn.className = btn.getAttribute('data-original-classes');
        btn.innerHTML = btn.getAttribute('data-original-content');
        btn.removeAttribute('data-original-content');
        this.frameTarget.removeEventListener('turbo:frame-load', restoreButton);
      };

      // Listen for frame load to restore button
      if (this.hasFrameTarget) {
        this.frameTarget.addEventListener('turbo:frame-load', restoreButton);
      }

      // Force a reflow to ensure the class changes are applied
      void btn.offsetWidth;
    }
  }

  prev() {
    if (this.disableNavigation) return;
    this.flashChevron('left');

    // Refresh the visible response IDs to account for any filtering changes
    this.refreshVisibleResponseIds();

    // For images view, we have deduplicated IDs, so just go to previous index
    if (this.frameTarget.id === 'images-response-modal') {
      const i = this.currentResponseIndex - 1;
      this.navigateToResponse(i);
    } else {
      // Original logic for other contexts (skip duplicates)
      let i = this.currentResponseIndex - 1;
      const currentId = this.responseIds[this.currentResponseIndex];
      while (i >= 0 && this.responseIds[i] === currentId) {
        i--;
      }
      this.navigateToResponse(i);
    }
  }

  next() {
    if (this.disableNavigation) return;
    this.flashChevron('right');

    // Refresh the visible response IDs to account for any filtering changes
    this.refreshVisibleResponseIds();

    // For images view, we have deduplicated IDs, so just go to next index
    if (this.frameTarget.id === 'images-response-modal') {
      const i = this.currentResponseIndex + 1;
      this.navigateToResponse(i);
    } else {
      // Original logic for other contexts (skip duplicates)
      let i = this.currentResponseIndex + 1;
      const currentId = this.responseIds[this.currentResponseIndex];
      while (i < this.responseIds.length && this.responseIds[i] === currentId) {
        i++;
      }
      this.navigateToResponse(i);
    }
  }

  prevHistory() {
    if (this.disableNavigation) return;
    const prevId = this.frameTarget.dataset.prevCampaignHistoryId;
    if (!prevId) return;

    this.flashHistoryChevron('left');
    this.triggerTurboFrameReload(prevId);
    localStorage.setItem("openModalResponseId", prevId);
    this.highlightCurrentResponse();
  }

  nextHistory() {
    if (this.disableNavigation) return;
    const nextId = this.frameTarget.dataset.nextCampaignHistoryId;
    if (!nextId) return;

    this.flashHistoryChevron('right');
    this.triggerTurboFrameReload(nextId);
    localStorage.setItem("openModalResponseId", nextId);
    this.highlightCurrentResponse();
  }

  flashHistoryChevron(direction) {
    let btn = null;
    if (direction === 'left' && this.hasPrevHistoryButtonTarget) {
      btn = this.prevHistoryButtonTarget;
    } else if (direction === 'right' && this.hasNextHistoryButtonTarget) {
      btn = this.nextHistoryButtonTarget;
    }
    if (btn) {
      // Save original classes and content
      const originalClasses = btn.getAttribute('data-original-classes') || btn.className;
      const originalContent = btn.innerHTML;
      btn.setAttribute('data-original-classes', originalClasses);
      btn.setAttribute('data-original-content', originalContent);

      // Replace content with spinner
      btn.innerHTML = `
        <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      `;

      // Add loading state classes
      btn.classList.remove('bg-white/80', 'text-gray-700', 'hover:bg-white');
      btn.classList.add('bg-sapphire', 'text-white');

      // Restore original state after turbo frame loads
      const restoreButton = () => {
        btn.className = btn.getAttribute('data-original-classes');
        btn.innerHTML = btn.getAttribute('data-original-content');
        btn.removeAttribute('data-original-content');
        this.frameTarget.removeEventListener('turbo:frame-load', restoreButton);
      };

      // Listen for frame load to restore button
      if (this.hasFrameTarget) {
        this.frameTarget.addEventListener('turbo:frame-load', restoreButton);
      }

      // Force a reflow to ensure the class changes are applied
      void btn.offsetWidth;
    }
  }

  handleKeyDown(event) {
    if (this.disableNavigation) return;

    // Disable navigation when in edit mode (lock icon is not visible)
    const lockIcon = document.getElementById('response-lock-icon');
    if (!lockIcon || !lockIcon.offsetParent) {
      // Lock icon is not visible or doesn't exist, meaning we're in edit mode
      return;
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      this.prev();
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      this.next();
    }
  }

  updateNavButtons() {
    // Exit early if nav buttons don't exist yet
    if (!this.hasPrevButtonTarget || !this.hasNextButtonTarget) {
      return;
    }

    if (this.hasPrevButtonTarget) {
      const canGoPrev = !this.disableNavigation && this.currentResponseIndex > 0;

      if (!canGoPrev) {
        this.prevButtonTarget.classList.add('hidden');
      } else {
        this.prevButtonTarget.classList.remove('hidden');
      }
      this.prevButtonTarget.disabled = !canGoPrev;
    }

    if (this.hasNextButtonTarget) {
      const canGoNext = !this.disableNavigation && this.currentResponseIndex < this.responseIds.length - 1;

      if (!canGoNext) {
        this.nextButtonTarget.classList.add('hidden');
      } else {
        this.nextButtonTarget.classList.remove('hidden');
      }
      this.nextButtonTarget.disabled = !canGoNext;
    }

    // Update campaign history buttons visibility
    if (this.hasPrevHistoryButtonTarget) {
      const hasPrevHistory = this.frameTarget.dataset.prevCampaignHistoryId;
      if (!hasPrevHistory) {
        this.prevHistoryButtonTarget.classList.add('hidden');
      } else {
        this.prevHistoryButtonTarget.classList.remove('hidden');
      }
      this.prevHistoryButtonTarget.disabled = !hasPrevHistory;
    }

    if (this.hasNextHistoryButtonTarget) {
      const hasNextHistory = this.frameTarget.dataset.nextCampaignHistoryId;
      if (!hasNextHistory) {
        this.nextHistoryButtonTarget.classList.add('hidden');
      } else {
        this.nextHistoryButtonTarget.classList.remove('hidden');
      }
      this.nextHistoryButtonTarget.disabled = !hasNextHistory;
    }
  }
}